import * as React from "react";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    Switch
} from "@mui/material";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import {useTranslation} from "react-i18next";
import {useEntity} from "crqlar-react-common/dist/components/useEntity";
import {ResourceService} from "crqlar-react-common/dist/services/resource/resource.service";
import {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import EntityActions from "crqlar-react-common/dist/components/EntityActions";
import Property from "crqlar-react-common/dist/components/Property";
import {useQueryClient} from "react-query";

interface Props {
    open: boolean
    parentId: string
    onClose: () => void
}

const AddChildResourceDialog = ({open, parentId, onClose}: Props) => {
    const {t} = useTranslation()
    const queryClient = useQueryClient()

    const resourceData = useEntity({
        get: async () => ({ parentId }),
        create: ResourceService.createResource,
        update: ResourceService.updateResource,
        delete: (id) => ResourceService.deleteResource({id: [id]}),
        onDeleted: onClose,
        onSaved: () => {
            queryClient.invalidateQueries('allResources', {exact: false})
            onClose()
        },
        showSaveSuccess: true,
        isValid: (resource) => !!resource.name,
        cleanupBeforeSave: (resource) => ({
            ...resource,
            name: resource.name?.trim(),
            meta: {
                ...resource.meta,
                bookableExternally: resource.meta?.bookableExternally ?? false,
                autoAssignExternally: resource.meta?.autoAssignExternally ?? false,
            }
        })
    }, 'new')

    const resource = resourceData.item
    const setResource = resourceData.setItem

    const updateField = (field: keyof Resource, value: any) => {
        setResource({...resource, [field]: value})
    }

    const updateMetaField = (field: string, value: any) => {
        updateField('meta', {...resource.meta, [field]: value})
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">{t('Add child resource')}</Typography>
                    <Stack direction="row" gap={1}>
                        <EntityActions data={resourceData} />
                        <Button variant="outlined" onClick={onClose}>{t('Cancel')}</Button>
                    </Stack>
                </Stack>
            </DialogTitle>

            <DialogContent>
                <Stack gap={1} mt={1}>
                    <Property
                        title={t('Name')}
                        tooltip={t('Display label for this child resource on the floor plan and in booking screens.')}
                    >
                        <TextField
                            size="small"
                            value={resource.name || ''}
                            onChange={(e) => updateField('name', e.target.value)}
                            fullWidth
                            required
                            autoFocus
                        />
                    </Property>

                    <Divider />

                    <Property
                        title={t('Capacity')}
                        tooltip={t('Number of seats or guests this child resource can hold.')}
                    >
                        <TextField
                            size="small"
                            type="number"
                            value={resource.capacity ?? 0}
                            onChange={(e) => updateField('capacity', parseInt(e.target.value))}
                            fullWidth
                        />
                    </Property>

                    <Divider />

                    <Property
                        title={t('Auto assign when booked through booking engine')}
                        tooltip={t('Allows externally booked reservations to use this child resource automatically.')}
                    >
                        <Switch
                            checked={resource?.meta?.autoAssignExternally ?? false}
                            onChange={(e) => updateMetaField('autoAssignExternally', e.target.checked)}
                        />
                    </Property>

                    <Divider />

                    <Property
                        title={t('Bookable through widget')}
                        tooltip={t('Makes this child resource selectable or usable in the guest booking widget.')}
                    >
                        <Switch
                            checked={resource?.meta?.bookableExternally ?? false}
                            onChange={(e) => updateMetaField('bookableExternally', e.target.checked)}
                        />
                    </Property>
                </Stack>
            </DialogContent>
        </Dialog>
    )
}

export default AddChildResourceDialog
